import moment from "moment-timezone";

/**
 * Which start times a rental may begin at on a given day.
 *
 * Shared because three surfaces need the identical answer and any drift between
 * them is a customer-visible dead end: the widget checkout grid, the dashboard
 * reschedule picker, and (as the authority) the server's
 * `windowWithinOperatingHours`. A picker that offers a time the server refuses
 * is the shape of bug 2; a picker that hides a time the server allows silently
 * loses the partner bookings.
 *
 * Everything here is in minutes-from-midnight, local to the listing's timezone.
 * Callers own the conversion to real instants, because the widget builds them
 * from a calendar selection and the dashboard from an ISO string.
 */

/** A bookable stretch of a single day. */
export type OpeningWindow = { startMin: number; endMin: number };

/**
 * Fallback window used when a listing has no usable operating hours.
 *
 * The server returns `true` from `windowWithinOperatingHours` when nothing is
 * configured ("can't reason → defer"), so callers must offer the full day in
 * exactly that case. Narrowing it would refuse bookings that currently succeed.
 */
export const RENTAL_FALLBACK_DAY_START_MIN = 6 * 60; // 06:00
export const RENTAL_FALLBACK_DAY_END_MIN = 22 * 60; // 22:00

type ScheduleSlot = {
  status?: string;
  isoWeekday?: number | null;
  startPeriod?: { hour: number; minute: number } | null;
  endPeriod?: { hour: number; minute: number } | null;
  /**
   * Which tickets the partner assigned to this slot, as written by the
   * availability page (`editEventAppointments` → `tickets[].ticketTypes`).
   * Optional so every existing fixture and caller stays assignable.
   */
  tickets?: ({ ticketTypes?: (string | null)[] | null } | null)[] | null;
};

/**
 * Which rental ticket we are resolving hours FOR, and the universe it lives in.
 *
 * `rentalTicketIds` must be supplied by the server — it cannot be derived on the
 * client. `GetCourseWithBundles` filters a listing's tickets PER BUYER (tag
 * gating, prior-purchase hiding, status/visibility), so a widget deriving the
 * set from what it can see would disagree with the server for some buyers, and a
 * picker that disagrees with the server is the dead end this whole module exists
 * to prevent.
 */
export type RentalTicketFilter = {
  /** The ticket being booked. Absent ⇒ no ticket filtering at all. */
  ticketId?: string | null;
  /** Every RENTAL ticket id on this listing, whoever is looking. */
  rentalTicketIds?: readonly string[] | null;
};

/**
 * The slots a given rental ticket may be booked into.
 *
 * ⚠ The obvious rule — "keep slots naming this ticket" — is WRONG against real
 * data, and would take a live listing from seven bookable days to one.
 *
 * `rent-a-bike-for-an-hour` is configured like this:
 *
 * ```
 *   Mon      tickets = [ "Test ticket"  ]   ← bookingType: rental, has a pool
 *   Tue-Sun  tickets = [ "Standard"     ]   ← bookingType: regular, no pool
 * ```
 *
 * The rental checkout can only ever sell "Test ticket" (it keeps tickets with a
 * `rentalFractionMinutes`), so naming "Standard" on Tue-Sun is not the partner
 * saying "no hires on Tuesday" — it is a leftover from the class side of the
 * listing. Filtering to slots that name the rental ticket leaves Monday alone.
 *
 * So eligibility is decided PER SLOT, and only another RENTAL ticket counts as a
 * real assignment:
 *
 * | the slot names…                        | eligible? |
 * | -------------------------------------- | --------- |
 * | this rental ticket                     | yes       |
 * | a DIFFERENT rental ticket              | no        |
 * | only non-rental tickets, or nothing    | yes — wildcard |
 *
 * The wildcard arm is what keeps existing listings whole: a slot that names no
 * rental ticket constrains nothing, so it stays open to every rental ticket.
 * Narrowing only ever happens because a partner deliberately put a rental ticket
 * on a day.
 */
export function selectSlotsForRentalTicket<T extends ScheduleSlot>(
  slots: T[],
  filter?: RentalTicketFilter,
): T[] {
  const ticketId = filter?.ticketId;
  if (!ticketId) return slots;

  // Without the universe we cannot tell "assigned to another rental ticket"
  // from "names a class ticket", and guessing in either direction is a
  // customer-visible error. Defer, exactly as the rest of this module does.
  const rentalIds = filter?.rentalTicketIds;
  if (!rentalIds || rentalIds.length === 0) return slots;
  const isRentalTicket = new Set(rentalIds.map(String));

  return slots.filter((slot) => {
    const named = (slot?.tickets ?? [])
      .flatMap((t) => t?.ticketTypes ?? [])
      .filter((id): id is string => typeof id === "string" && id.length > 0);

    if (named.some((id) => id === ticketId)) return true;
    // Wildcard: nothing here is a rental assignment, so it constrains nothing.
    return !named.some((id) => isRentalTicket.has(id));
  });
}

export type RentalOperatingHours = {
  /** Bookable windows per JS weekday (0 = Sunday). */
  byWeekday: Map<number, OpeningWindow[]>;
  /**
   * How many slots the SERVER counts as configured.
   *
   * Deliberately NOT `byWeekday.size`. The server's enforce-or-defer switch is
   * `slots.length === 0` after filtering on status/isoWeekday/periods only, so
   * the two counts can differ and the client must measure the same thing the
   * server does. If it judged by the window map instead it would see nothing,
   * apply the full-day fallback, and offer a complete grid of times the server
   * rejects — the listing would look fully bookable and be entirely unbookable.
   *
   * The example this used to give — an overnight 20:00→02:00 "which neither
   * side can satisfy" — is no longer one: both sides now roll such a window
   * forward a day and it is fully bookable. The field still earns its keep for
   * the ticket-narrowing case, where selectSlotsForRentalTicket can empty
   * `byWeekday` on a listing that is configured.
   */
  configuredSlotCount: number;
};

/**
 * The listing's enabled opening windows, keyed by JS weekday (0 = Sunday).
 *
 * Mirrors the server's filter exactly — same `status === "enabled"` test, same
 * required fields, same ISO-7-to-JS-0 mapping — and reports separately how many
 * slots the server considers configured, because the two questions differ for
 * malformed windows. See {@link RentalOperatingHours.configuredSlotCount}.
 */
export function readRentalOperatingHours(
  appointmentScheduleSlots: ScheduleSlot[] | null | undefined,
  filter?: RentalTicketFilter,
): RentalOperatingHours {
  const byWeekday = new Map<number, OpeningWindow[]>();

  // Two passes, and the two counts answer DIFFERENT questions.
  //
  // `usable` is every slot the SERVER considers configured — status, isoWeekday,
  // periods, and nothing else. That is exactly the set
  // `windowWithinOperatingHours` measures its enforce-or-defer switch on.
  const usable = (appointmentScheduleSlots ?? []).filter(
    (slot) =>
      slot?.status === "enabled" &&
      slot?.isoWeekday != null &&
      !!slot?.startPeriod &&
      !!slot?.endPeriod,
  );

  // ⚠ `configuredSlotCount` is the UNFILTERED count, deliberately.
  //
  // An earlier version counted the narrowed set with a comment claiming
  // `selectSlotsForRentalTicket` could never empty a non-empty input. That was
  // FALSE: the wildcard arm only rescues a slot naming no rental ticket, so a
  // ticket whose every slot belongs to a DIFFERENT rental ticket narrowed to
  // zero. The client then read "nothing configured", applied the 06:00–22:00
  // full-day fallback and offered a complete grid — while the server, measuring
  // its defer on the unfiltered set, refused every one of those windows and
  // quoted no hours. Fully bookable-looking, fully unbookable: the precise
  // inversion this field exists to prevent, reintroduced by the field itself.
  //
  // Keeping it unfiltered makes the two sides agree by construction. "Are hours
  // enforced at all?" is a property of the LISTING; which windows a ticket gets
  // is a separate question, and that one is answered by `byWeekday` — an empty
  // map for a fully-assigned-elsewhere ticket, which `openingWindowsForWeekday`
  // reports as genuinely closed rather than wide open.
  const configuredSlotCount = usable.length;

  for (const slot of selectSlotsForRentalTicket(usable, filter)) {
    if (
      slot?.isoWeekday == null ||
      !slot?.startPeriod ||
      !slot?.endPeriod
    ) {
      continue;
    }

    const day = slot.isoWeekday === 7 ? 0 : slot.isoWeekday;
    const window = {
      startMin: slot.startPeriod.hour * 60 + slot.startPeriod.minute,
      endMin: slot.endPeriod.hour * 60 + slot.endPeriod.minute,
    };
    // A window that crosses midnight (22:00 -> 02:00) or names the whole day
    // (09:00 -> 09:00) rolls forward, so its end may exceed 1440.
    //
    // This used to `continue`, on the stated grounds that "the server can't
    // satisfy it either — it builds both bounds on the start's own day, so an
    // overnight slot never matches". That was true when it was written and is
    // not true here: windowWithinOperatingHours rolls slotEnd forward a day for
    // an inverted slot and accepts the booking, with a test pinning a Wed 23:30
    // hire running to Thu 01:30. Dropping the window therefore made a listing
    // whose only slot is overnight report "not available on any day" on EVERY
    // day, while the server would have sold it — the picker hiding a time the
    // server allows, which is the failure this module exists to prevent.
    //
    // Rolling here mirrors the server exactly, including the zero-length case
    // it also treats as a full day. Nothing validates these fields on any write
    // path (free-typed pickers, `z.string().min(1)`, no route middleware, bare
    // Numbers on the model), and "copy times to other days" fans one such slot
    // out to seven — so this is ordinary partner input, not corrupt data.
    if (window.endMin <= window.startMin) window.endMin += 24 * 60;
    byWeekday.set(day, [...(byWeekday.get(day) ?? []), window]);
  }

  return { byWeekday, configuredSlotCount };
}

/**
 * Bookable windows for one weekday. Distinguishes the two empties that matter:
 * nothing configured anywhere → the full-day fallback (server enforces nothing);
 * hours configured but none bookable on this day → `[]`, genuinely closed.
 */
export function openingWindowsForWeekday(
  operatingHours: RentalOperatingHours,
  jsWeekday: number,
): OpeningWindow[] {
  if (operatingHours.configuredSlotCount === 0) {
    return [
      {
        startMin: RENTAL_FALLBACK_DAY_START_MIN,
        endMin: RENTAL_FALLBACK_DAY_END_MIN,
      },
    ];
  }
  return operatingHours.byWeekday.get(jsWeekday) ?? [];
}

/**
 * Grid-aligned start times (minutes from midnight) that fit a whole booking
 * inside a single opening window.
 *
 * A booking may not span two windows: on a 09:00–12:00 / 14:00–17:00 day an
 * 11:00 two-hour rental is not offered, because the server requires the whole
 * window to sit inside one slot.
 */
export function rentalStartOptions(params: {
  windows: OpeningWindow[];
  durationMinutes: number;
  fractionMinutes: number;
}): number[] {
  const { windows, durationMinutes, fractionMinutes } = params;
  if (!durationMinutes || !fractionMinutes || fractionMinutes <= 0) return [];

  const opts: number[] = [];
  for (const w of windows) {
    // `m < 24 * 60` is load-bearing, not defensive. A rolled overnight window
    // (22:00 -> 26:00) would otherwise offer 1440 as a start: formatMinutesOfDay
    // renders it "24:00", and the server refuses it on the weekday test, since
    // the instant belongs to the NEXT day. That would replace one dead end with
    // another. Starts stay on the slot's own day; only the END may run past
    // midnight, which is exactly what the server permits.
    for (
      let m = w.startMin;
      m + durationMinutes <= w.endMin && m < 24 * 60;
      m += fractionMinutes
    ) {
      opts.push(m);
    }
  }
  return [...new Set(opts)].sort((a, b) => a - b);
}

/** "09:00" for 540. */
export function formatMinutesOfDay(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * The instant at `minutesOfDay` wall-clock on `date`'s day, in `timezone`.
 *
 * The inverse of the grid above, and it must be built by SETTING the hour and
 * minute rather than ADDING elapsed minutes to local midnight. On a DST
 * transition the two disagree by an hour: adding 540 real minutes to midnight
 * on the day BST starts lands at 10:00, so a picker built that way offered
 * 10:00–17:00 for a 09:00–17:00 shop, made the legal first slot unselectable,
 * and let the last entry submit a time the server refuses.
 *
 * Mirrors how the server's `windowWithinOperatingHours` builds its own bounds.
 */
export function wallClockInstant(params: {
  date: string | number | Date;
  timezone: string;
  minutesOfDay: number;
}): Date {
  const { date, timezone, minutesOfDay } = params;
  const startOfDay = moment.tz(date, timezone).startOf("day");
  const set = startOfDay
    .clone()
    .hour(Math.floor(minutesOfDay / 60))
    .minute(minutesOfDay % 60)
    .second(0)
    .millisecond(0);

  // ⚠ On a spring-forward day the requested time may NOT EXIST.
  //
  // UK clocks jump 01:00 → 02:00, so 01:00–01:59 is not a real local time that
  // day, and moment resolves a gap time by normalising BACKWARD: asking for
  // 01:00 hands back 00:00. Setting the hour is still right — it is what makes
  // 09:00 mean 09:00 across a boundary, which is why this function exists — but
  // it silently returns a time EARLIER than the one requested, and four
  // distinct requests (60/75/90/105) collapse onto the same four instants as
  // 0/15/30/45.
  //
  // Resolve forward instead, which is what the clock itself did: the moment
  // that would have been 01:30 is 02:30. Elapsed-addition from local midnight
  // lands exactly there — the one case where the "add real minutes" arithmetic
  // this function exists to replace gives the right answer, because the gap is
  // precisely the hour it double-counts.
  //
  // Callers building a LIST of options should not rely on this: a shifted 01:00
  // then collides with the genuine 02:00. Filter with `wallClockTimeExists`
  // first — the times simply are not offerable that day.
  const roundTrip = set.hours() * 60 + set.minutes();
  if (roundTrip !== minutesOfDay) {
    return startOfDay.clone().add(minutesOfDay, "minutes").toDate();
  }

  return set.toDate();
}

/**
 * Whether `minutesOfDay` is a real wall-clock time on that day in that zone.
 *
 * False only inside a DST spring-forward gap — 01:00–01:59 on the day UK clocks
 * jump to BST. Autumn fall-back times are ambiguous rather than missing, so they
 * stay valid (moment picks the first occurrence, and it round-trips).
 *
 * Exists so option lists can OMIT times that do not exist, rather than render
 * two entries resolving to one instant. `wallClockInstant` alone cannot express
 * this: it must return a Date for its other callers, so it resolves forward and
 * lets the list-builders ask this question separately.
 */
export function wallClockTimeExists(params: {
  date: string | number | Date;
  timezone: string;
  minutesOfDay: number;
}): boolean {
  const { date, timezone, minutesOfDay } = params;
  const set = moment
    .tz(date, timezone)
    .startOf("day")
    .hour(Math.floor(minutesOfDay / 60))
    .minute(minutesOfDay % 60)
    .second(0)
    .millisecond(0);
  return set.hours() * 60 + set.minutes() === minutesOfDay;
}

/**
 * How long a hire lasts, in words — "3 hours", "1 hour 30 minutes",
 * "45 minutes". Empty string for a missing or non-positive duration.
 *
 * Shared because it is rendered on both sides of the same booking: the
 * customer's confirmation email and the partner's booking detail page. Those
 * grew separate implementations that disagreed — the email said "2 hours 30
 * minutes" where the dashboard said "2h 30m" for the same hire — so a partner
 * reading a customer's email back to them was quoting different text for the
 * same field. Lives here rather than in the API because the dashboard cannot
 * import from apps/api.
 */
export function formatRentalDuration(minutes: number): string {
  if (!minutes || minutes <= 0) return "";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (mins > 0) parts.push(`${mins} ${mins === 1 ? "minute" : "minutes"}`);
  return parts.join(" ");
}
